import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, LogIn, Shield } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function Login() {
  const { login, facebookMockLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const from = location.state?.from || "/cuoc-thi";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/cuoc-thi";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onFacebook = async () => {
    setError("");
    setLoading(true);
    try {
      await facebookMockLogin();
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-24 pb-12 min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-30" />
      <div
        className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-20"
        style={{
          background: "radial-gradient(circle, #06B6D4 0%, transparent 70%)",
        }}
      />

      <div className="container-x relative max-w-md">
        <div className="text-center mb-8 fade-up">
          <Shield className="w-12 h-12 text-[var(--gold-deep)] mx-auto mb-3" />
          <h1 className="font-heading text-3xl font-bold">
            Đăng nhập hệ thống
          </h1>
          <p className="text-slate-500 text-sm mt-2">Cục Dân số · Bộ Y tế</p>
        </div>

        <div className="card p-8 fade-up delay-1">
          <div className="space-y-3 mb-6">
            <button
              onClick={onGoogle}
              className="w-full btn-secondary"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 48 48">
                <path
                  fill="#FFC107"
                  d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8a12 12 0 1 1 0-24c3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.6 6.5 29 4.5 24 4.5A19.5 19.5 0 1 0 43.6 24c0-1.2-.1-2.3-.4-3.5z"
                />
                <path
                  fill="#FF3D00"
                  d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.6 6.5 29 4.5 24 4.5 16.3 4.5 9.8 8.8 6.3 14.7z"
                />
                <path
                  fill="#4CAF50"
                  d="M24 43.5c5 0 9.5-1.9 12.9-5l-6-5.1C29 35 26.6 36 24 36c-5.2 0-9.7-3.3-11.3-8L6 32.9C9.4 39 16.1 43.5 24 43.5z"
                />
                <path
                  fill="#1976D2"
                  d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.6h0l6 5.1c-.4.4 6.4-4.7 6.4-14.2 0-1.2-.1-2.3-.4-3.5z"
                />
              </svg>
              Đăng nhập với Google
            </button>
            <button
              onClick={onFacebook}
              className="w-full btn-secondary"
              disabled={loading}
              data-testid="facebook-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
                <path d="M24 12.07A12 12 0 1 0 10.13 24v-8.5H7.08v-3.43h3.05V9.41c0-3 1.79-4.67 4.53-4.67 1.31 0 2.69.24 2.69.24v2.95h-1.51c-1.49 0-1.96.93-1.96 1.88v2.26h3.32l-.53 3.43h-2.79V24A12 12 0 0 0 24 12.07z" />
              </svg>
              Đăng nhập với Facebook
              <span className="text-[10px] text-slate-500 ml-1">(Demo)</span>
            </button>
          </div>

          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-[var(--bg-alt)]" />
            <span className="text-xs text-slate-500 uppercase tracking-widest">
              hoặc
            </span>
            <div className="flex-1 h-px bg-[var(--bg-alt)]" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input pl-10"
                  placeholder="email@example.com"
                  data-testid="email-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">
                Mật khẩu
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pl-10"
                  placeholder="••••••••"
                  data-testid="password-input"
                />
              </div>
            </div>

            {error && (
              <div
                className="text-red-400 text-sm bg-red-400/10 border border-red-400/30 rounded-lg p-3"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary"
              data-testid="login-submit-btn"
            >
              <LogIn className="w-4 h-4" />{" "}
              {loading ? "Đang đăng nhập…" : "Đăng nhập"}
            </button>
          </form>

          <div className="text-center text-sm text-slate-500 mt-6">
            Chưa có tài khoản?{" "}
            <Link
              to="/dang-ky"
              className="text-[var(--gold-deep)] hover:underline"
              data-testid="link-register"
            >
              Đăng ký ngay
            </Link>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-[var(--border)] bg-white/80 p-5 text-sm text-slate-600 shadow-sm backdrop-blur">
          <p className="mb-3 text-center text-xs font-semibold uppercase tracking-widest text-slate-500">
            Tài khoản mẫu để thử nghiệm
          </p>
          <div className="space-y-2">
            <div className="flex flex-col gap-1 rounded-xl bg-slate-50 p-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="font-semibold text-slate-700">Admin</span>
              <span className="font-mono text-xs text-slate-600">
                admin@dansothi.gov.vn / Admin@123
              </span>
            </div>
            <div className="flex flex-col gap-1 rounded-xl bg-slate-50 p-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="font-semibold text-slate-700">BTC</span>
              <span className="font-mono text-xs text-slate-600">
                btc@dansothi.gov.vn / Btc@123
              </span>
            </div>
            <div className="flex flex-col gap-1 rounded-xl bg-slate-50 p-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="font-semibold text-slate-700">BGK</span>
              <span className="font-mono text-xs text-slate-600">
                bgk@dansothi.gov.vn / Jury@123
              </span>
            </div>
            <div className="flex flex-col gap-1 rounded-xl bg-slate-50 p-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="font-semibold text-slate-700">Thí sinh</span>
              <span className="font-mono text-xs text-slate-600">
                thisinh@example.com / User@123
              </span>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
